// The send form for the templates the shared component can't send (GIF, location, LTO, any voice-call template) and, behind
// the host's own-form flag, every template isLocalFormSupported accepts (routing: ../registry.js, import rules: ../README.md).
// It mirrors the library's regular form field for field and string for string (TemplateMessaging.tsx's variable fields, its
// dynamic-button block and its COMPLEX "Button Details" block, MediaScreen for the media, the checks of Components/Footer),
// so a kind can go back to the library unseen; __parity__/ holds it to that. As in the Footer, a refused Save shows
// the toast and no text on the field. Only location and the LTO expiry, which the library has no fields for, carry a message.
import { useState } from 'react';
import { TextField, Typography } from '@gupshup-frontend/gupshup-ts-components';
import SendPanelShell from '../shared/SendPanelShell';
import FreeTextInput from '../shared/FreeTextInput';
import { useReportValues } from '../shared/useReportValues';
import MediaField from './MediaField';
import {
  validateFreeText, validateMedia, validateDynamicButton, MEDIA_TOKEN_RE, DOUBLE_BRACE_RE, DOUBLE_BRACE_ERROR,
} from '../shared/validators';
import { buildSubmitData } from '../shared/submitData';
import { dynamicButtonValue } from '../shared/draftValues';
import {
  describeTemplate, SEND_MEDIA_KINDS, MESSAGE_TYPE_BY_HEADER_KIND, DYNAMIC_BUTTON_FIELDS, buttonInputKind, buttonsTravelAsJson,
} from '../shared/templateModel';
import { buildButtonComponents, parseButtonComponents } from './buildButtonComponents';
import {
  buildLtoTemplateJson, parseLtoTemplateJson, activeLtoExpiryAsString, LTO_EXPIRY_AS_STRING,
} from './buildLtoTemplateJson';

// The library's DYNAMIC_HELP_TEXT (Helper/Constants.tsx:13-17), character for character: the DYNAMIC text has a
// line break and three spaces in it. Its getHelperText (Helper/Utils.tsx:408-416) hands these same two strings to a
// URL button of the Button Details block.
const DYNAMIC_HELP_TEXT = {
  DYNAMIC: 'The dynamic suffix of the button URL for a Call-to-Action Interactive Button template\n   of type “Visit Website” where the URL is dynamic. The static prefix of the button URL must be specified at the time of template creation. E.g., developer/home.',
  DYNAMIC_LINK_TRACK: 'Please provide the full url you want the user to be redirected to on clicking the shorten url received in the message.',
};

function getHelperText(button) {
  if (button.type !== 'URL') return '';
  return button.urlType === 'DYNAMIC' ? DYNAMIC_HELP_TEXT.DYNAMIC : DYNAMIC_HELP_TEXT.DYNAMIC_LINK_TRACK;
}

// getLabelFromButtonType (Helper/Utils.tsx:398-406), plus voice_call: the library has no voice-call button, and its
// default case would print the raw "voice_call".
const BUTTON_TYPE_LABELS = {
  copy_code: 'Copy Code', quick_reply: 'Quick Reply', url: 'URL', phone_number: 'Phone Number', voice_call: 'Voice Call',
};
// TemplateMessaging.tsx:732-735 — st, nd, rd, then th for the fourth button on.
const ORDINAL_SUFFIXES = ['st', 'nd', 'rd'];

// The label of a Button Details row (TemplateMessaging.tsx:746-748), leading space included.
function buttonRowLabel(button) {
  const type = button.type?.toLowerCase();
  return ` ${BUTTON_TYPE_LABELS[type] ?? type} - ${button.index + 1}${ORDINAL_SUFFIXES[button.index] ?? 'th'} Button`;
}

// A button that takes a typed value (templateModel's buttonInputKind); every other kind is a disabled row in
// Button Details.
function needsButtonInput(button) {
  return buttonInputKind(button) !== null;
}

/**
 * Latitude/longitude check: a single `{token}`, or a plain number within [min, max].
 * @param {string} value
 * @param {number} min
 * @param {number} max
 * @param {string} label - used in the error message
 * @returns {string} empty string when valid, else an error message
 */
function validateCoordinate(value, min, max, label) {
  if (!value || !String(value).trim()) return `${label} is required.`;
  const trimmed = String(value).trim();
  if (MEDIA_TOKEN_RE.test(trimmed)) return '';
  if (DOUBLE_BRACE_RE.test(trimmed)) return DOUBLE_BRACE_ERROR;
  const num = Number(trimmed);
  if (Number.isNaN(num) || num < min || num > max) {
    return `Enter a number between ${min} and ${max}, or a single token like {customer.${label.toLowerCase()}}.`;
  }
  return '';
}

const checkBraces = (value) => (DOUBLE_BRACE_RE.test(value || '') ? DOUBLE_BRACE_ERROR : '');

// The location fields, in screen order. The library has none, so each carries its own help text and check.
const LOCATION_FIELDS = [
  {
    key: 'latitude',
    label: 'Latitude',
    required: true,
    help: 'A number between -90 and 90, or a customer attribute such as {customer.latitude}.',
    check: (value) => validateCoordinate(value, -90, 90, 'Latitude'),
  },
  {
    key: 'longitude',
    label: 'Longitude',
    required: true,
    help: 'A number between -180 and 180, or a customer attribute such as {customer.longitude}.',
    check: (value) => validateCoordinate(value, -180, 180, 'Longitude'),
  },
  {
    key: 'name', label: 'Name', help: 'Optional — the location label, e.g. a store name.', check: checkBraces,
  },
  {
    key: 'address', label: 'Address', help: 'Optional — the street address.', check: checkBraces,
  },
];

// epoch ms -> "YYYY-MM-DDTHH:mm" in LOCAL time, the value <input type="datetime-local"> takes.
function msToDatetimeLocalValue(ms) {
  if (ms === null || ms === undefined) return '';
  const date = new Date(ms);
  if (Number.isNaN(date.getTime())) return '';
  const pad = (n) => String(n).padStart(2, '0');
  return `${date.getFullYear()}-${pad(date.getMonth() + 1)}-${pad(date.getDate())}T${pad(date.getHours())}:${pad(date.getMinutes())}`;
}

// Inverse — a bare "YYYY-MM-DDTHH:mm" string (no offset) parses as LOCAL time, same as the input's
// own value.
function datetimeLocalValueToMs(value) {
  if (!value) return null;
  const ms = new Date(value).getTime();
  return Number.isNaN(ms) ? null : ms;
}

/**
 * @typedef {object} StandardSendPanelProps
 * @property {{ details: object }} template - list-row template; details.type/header/body/buttons/
 *   button_type/limitedTimeOffer are read via ../shared/templateModel.js's describeTemplate
 * @property {string} channelId
 * @property {string} orgId
 * @property {(data: object) => boolean} onSubmit - see ../shared/submitData.js for the payload shape
 * @property {() => void} onBack
 * @property {{ params?: string[], headerParams?: string[], mediaUrl?: string, location?: object, waTemplateJson?: object, buttonUrlParam?: string, ctaButtonUrl?: string }|null} restored - null for a fresh pick
 * @property {boolean} [disabled]
 * @property {{ footerComponent?: import('react').ReactNode, sendButtonText?: string }} [customisations]
 * @property {(data: object) => void} [onValuesChange] - called once after mount and after every change to a
 *   value that feeds the submit payload, with the object Save would pass to onSubmit, built without
 *   validation (a half-filled form is reported as it stands)
 * @property {(file: File) => Promise<(string|null)>} [onUpload] - uploads a media header file and resolves to
 *   its URL, or null when it can't (the host reports that failure). Without it the media field is a URL box only.
 * @property {boolean} [allowMediaTokens] - a whole `{token}` may stand in for the media URL. Off by default, which is
 *   the library's rule (the value must be a URL): the webhook sends the media as typed. Turn it on once the webhook
 *   converts mediaUrl like any other field (fix/hide-cardless-carousel-note, 603d178).
 */

/**
 * @param {StandardSendPanelProps} props
 * @returns {JSX.Element}
 */
const StandardSendPanel = ({
  template, channelId, orgId, onSubmit, onBack, restored, disabled, customisations, onValuesChange, onUpload, allowMediaTokens = false,
}) => {
  const describe = describeTemplate(template?.details);
  const {
    headerKind, headerVarCount, bodyVarCount, buttons, isLto, ltoHasExpiration, buttonType,
  } = describe;
  // Needs a media URL field, distinct from isLocationHeader below — LOCATION is a media header for
  // classification but sends lat/lng, not a URL (see SEND_MEDIA_KINDS's own comment).
  const needsMediaUrl = SEND_MEDIA_KINDS.includes(headerKind);
  const isLocationHeader = headerKind === 'LOCATION';
  // COMPLEX, LTO and voice-call templates send their buttons as JSON, and get the library's Button Details block (the
  // library has it for COMPLEX only; it can't send the others).
  const buttonsAsJson = buttonsTravelAsJson(describe);
  // The single dynamic button is keyed on button_type alone, as in the library: a DYNAMIC row can come without a
  // buttons array, and an AUTHENTICATION row (STATIC) with a URL button gets no field. A voice-call template sends
  // its buttons whole, through Button Details.
  const dynamicButtonType = DYNAMIC_BUTTON_FIELDS[buttonType] && !buttonsAsJson ? buttonType : null;

  // Header slots first, then body: the library numbers them straight through.
  const [values, setValues] = useState(() => [
    ...Array.from({ length: headerVarCount }, (_, i) => (restored?.headerParams || [])[i] ?? ''),
    ...Array.from({ length: bodyVarCount }, (_, i) => (restored?.params || [])[i] ?? ''),
  ]);
  const [mediaUrl, setMediaUrl] = useState(() => restored?.mediaUrl || '');
  const [locationFields, setLocationFields] = useState(() => Object.fromEntries(
    LOCATION_FIELDS.map(({ key }) => [key, restored?.location?.[key] || '']),
  ));
  // The single dynamic button's value, whichever of the two fields a draft kept it in.
  const [dynamicButton, setDynamicButton] = useState(() => dynamicButtonValue(restored));
  const [buttonValues, setButtonValues] = useState(() => {
    if (!restored) return {};
    if (isLto) return parseLtoTemplateJson(restored.waTemplateJson, buttons).values;
    if (buttonsAsJson) return parseButtonComponents(restored.waTemplateJson?.components, buttons);
    return {};
  });
  const [ltoExpiry, setLtoExpiry] = useState(() => {
    if (!restored || !isLto || !ltoHasExpiration) return '';
    return msToDatetimeLocalValue(parseLtoTemplateJson(restored.waTemplateJson, buttons).expirationMs);
  });
  const [errors, setErrors] = useState({});
  const [errorToastOpen, setErrorToastOpen] = useState(false);
  // Bumped by a Save that passes its checks. The library clears its template there (TemplateMessaging.tsx:275-288), which
  // unmounts the preview; remounting it here brings a media URL that does not load back to the preview's "Sample Media".
  const [previewKey, setPreviewKey] = useState(0);

  const activeExpiryAsString = activeLtoExpiryAsString();
  const showLtoWireShapeNote = isLto && ltoHasExpiration && activeExpiryAsString !== LTO_EXPIRY_AS_STRING;

  // One entry per {{n}} slot of the template, whatever the state holds.
  const slots = Array.from({ length: headerVarCount + bodyVarCount }, (_, i) => values[i] ?? '');

  const clearError = (key) => {
    setErrors((prev) => {
      if (!prev[key]) return prev;
      const next = { ...prev };
      delete next[key];
      return next;
    });
  };

  const setSlot = (index, value) => {
    setValues((prev) => {
      const next = [...prev];
      next[index] = value;
      return next;
    });
  };

  const setLocationField = (field, value) => {
    setLocationFields((prev) => ({ ...prev, [field]: value }));
    clearError(`location.${field}`);
  };

  const setButtonValue = (index, value) => {
    setButtonValues((prev) => ({ ...prev, [index]: value }));
  };

  // null until the picker holds a valid date and time.
  const expirationMs = isLto && ltoHasExpiration ? datetimeLocalValueToMs(ltoExpiry) : null;

  // The payload Save submits, built from the values as they stand with no checks. handleSave runs its checks and then
  // submits this; onValuesChange reports it for a half-filled form, so an empty expiry goes out empty (buildLtoTemplateJson).
  const buildPayload = () => {
    let waTemplateJson;
    let subType;
    if (isLto) {
      waTemplateJson = buildLtoTemplateJson({
        hasExpiration: ltoHasExpiration,
        expirationMs,
        buttons,
        values: buttonValues,
        expiryAsString: activeExpiryAsString,
      });
      subType = 'LTO';
    } else if (buttonsAsJson) {
      waTemplateJson = { components: buildButtonComponents(buttons, buttonValues) };
      subType = 'COMPLEX';
    }

    return buildSubmitData({
      template,
      channelId,
      orgId,
      params: slots.slice(headerVarCount),
      headerParams: slots.slice(0, headerVarCount),
      messageType: MESSAGE_TYPE_BY_HEADER_KIND[headerKind] || 'text',
      waTemplateJson,
      subType,
      // The button's value goes in the field its kind keeps it in: buttonUrlParam or ctaButtonUrl.
      ...(dynamicButtonType ? { [DYNAMIC_BUTTON_FIELDS[dynamicButtonType].field]: dynamicButton } : {}),
      mediaUrl: needsMediaUrl ? mediaUrl.trim() : undefined,
      location: isLocationHeader ? { ...locationFields } : undefined,
      // The library's Footer names every document send this way (Components/Footer/index.tsx:120-122).
      extra: headerKind === 'DOCUMENT' ? { fileName: `doc_${new Date().toDateString()}.pdf` } : undefined,
    });
  };

  const handleSave = () => {
    // Inline messages: only the fields the library has no counterpart for (location, expiry) carry one.
    const nextErrors = {};

    // What the library's Footer refuses (Components/Footer/index.tsx:47-69): its toast and nothing on the field, as here.
    // The media has to be a URL unless the host allows a {token} (allowMediaTokens).
    const refused = [
      ...slots.map(validateFreeText),
      needsMediaUrl ? validateMedia(mediaUrl, headerKind, { urlOnly: !allowMediaTokens }) : '',
      dynamicButtonType ? validateDynamicButton(dynamicButton, dynamicButtonType) : '',
      ...(buttonsAsJson
        ? buttons.filter(needsButtonInput).map((button) => validateFreeText(buttonValues[button.index]))
        : []),
    ].some(Boolean);

    if (isLocationHeader) {
      LOCATION_FIELDS.forEach(({ key, check }) => {
        const error = check(locationFields[key]);
        if (error) nextErrors[`location.${key}`] = error;
      });
    }

    if (isLto && ltoHasExpiration) {
      if (!ltoExpiry) {
        nextErrors.ltoExpiry = 'This field is required.';
      } else if (expirationMs === null) {
        nextErrors.ltoExpiry = 'Enter a valid date and time.';
      } else if (expirationMs <= Date.now()) {
        nextErrors.ltoExpiry = 'Offer expiry must be in the future.';
      }
    }

    setErrors(nextErrors);
    if (refused || Object.keys(nextErrors).length > 0) {
      setErrorToastOpen(true);
      return;
    }

    setPreviewKey((key) => key + 1);
    onSubmit(buildPayload());
  };

  useReportValues(buildPayload(), onValuesChange);

  // A blank slot previews as its own {{n}}; anything else as typed, a lone space included (TemplateMessaging's
  // variableHandler falls back to the token only for an empty string).
  const previewVariables = slots.map((value, i) => (value !== '' ? value : `{{${i + 1}}}`));

  // Mirrors TemplateMessaging.tsx's own nic-tm-sl-template-container condition
  // (variableCount(message) || variableCount(header) || category !== "TEXT"), read through our
  // headerKind vocabulary — 'text'/'none' both correspond to the wire's type TEXT.
  const showSlContainer = bodyVarCount > 0 || headerVarCount > 0 || (headerKind !== 'text' && headerKind !== 'none');

  return (
    <SendPanelShell
      leftClassName={showSlContainer ? 'nic-tm-sl-template-container' : undefined}
      previewKey={previewKey}
      previewConfig={{
        templateObj: template?.details,
        showHeading: true,
        headingName: 'Preview',
        // As typed, like the library's inputField.media. needsMediaUrl guards a restored LOCATION draft's
        // leftover mediaUrl (pre-fix data) from showing a media preview that shouldn't exist for this header kind.
        mediaUrl: needsMediaUrl && mediaUrl ? mediaUrl : undefined,
        variables: previewVariables,
        width: '100%',
        height: '100%',
      }}
      onBack={onBack}
      onSave={handleSave}
      customisations={customisations}
      disabled={disabled}
      showInvalidToast={errorToastOpen}
      onCloseInvalidToast={() => setErrorToastOpen(false)}
      footerExtra={showLtoWireShapeNote ? (
        <Typography
          variant="caption"
          style={{ color: 'var(--br-text-muted, #6b7280)' }}
          data-testid="bloomreach-standard-send-lto-wire-shape-note"
        >
          {`Testing: expiration_time_ms sent as a ${activeExpiryAsString ? 'string' : 'number'} (set in this browser).`}
        </Typography>
      ) : null}
    >
      {needsMediaUrl && (
        <>
          <br />
          <MediaField
            kind={headerKind}
            value={mediaUrl}
            onChange={setMediaUrl}
            onUpload={onUpload}
            allowTokens={allowMediaTokens}
          />
          <br />
        </>
      )}

      {isLocationHeader && (
        <>
          <p className="nic-tm-sub-header-padding">Location</p>
          {LOCATION_FIELDS.map(({
            key, label, required, help,
          }) => (
            <div style={{ marginBottom: '16px' }} key={key}>
              <TextField
                fullWidth
                color="primary"
                label={label}
                required={required}
                value={locationFields[key]}
                disabled={disabled}
                error={Boolean(errors[`location.${key}`])}
                helperText={errors[`location.${key}`] || help}
                onChange={(e) => setLocationField(key, e.target.value)}
                data-testid={`bloomreach-standard-send-location-${key}`}
              />
            </div>
          ))}
        </>
      )}

      {slots.length > 0 && (
        <>
          <p className="nic-tm-sub-header-padding">Body</p>
          {/* One field per {{n}} slot, header slots first. The library's enterprise branch numbers them straight through
              under a single "Body" heading and gives them no helper text (TemplateMessaging.tsx:352-377). */}
          {slots.map((value, i) => (
            <FreeTextInput
              key={`slot-${i}`}
              style={{ marginBottom: '16px', display: 'flex', gap: '18px' }}
              label={`Enter content for {{${i + 1}}}`}
              value={value}
              onChange={(next) => setSlot(i, next)}
            />
          ))}
        </>
      )}

      {dynamicButtonType && (
        <FreeTextInput
          style={{ marginBottom: '16px' }}
          data-testid="bloomreach-standard-send-dynamic-button"
          label="Button URL Params"
          value={dynamicButton}
          helperText={DYNAMIC_HELP_TEXT[dynamicButtonType]}
          onChange={setDynamicButton}
        />
      )}

      {buttonsAsJson && (
        <div>
          <p style={{ marginBottom: '24px' }}>Button Details</p>
          {buttons.map((button) => (needsButtonInput(button) ? (
            <FreeTextInput
              key={`button-${button.index}`}
              style={{ display: 'flex', gap: '12px', marginBottom: '24px' }}
              data-testid={`bloomreach-standard-send-button-${button.index}`}
              label={buttonRowLabel(button)}
              value={buttonValues[button.index] ?? ''}
              helperText={getHelperText(button)}
              onChange={(value) => setButtonValue(button.index, value)}
            />
          ) : (
            <TextField
              key={`button-${button.index}`}
              fullWidth
              style={{ marginBottom: '24px' }}
              disabled
              label={buttonRowLabel(button)}
              value={button.text ?? ''}
              InputLabelProps={{ shrink: true }}
              size="small"
              data-testid={`bloomreach-standard-send-button-${button.index}`}
            />
          )))}
        </div>
      )}

      {isLto && ltoHasExpiration && (
        <div style={{ marginBottom: '16px' }}>
          <TextField
            fullWidth
            color="primary"
            type="datetime-local"
            label="Offer expires at"
            required
            InputLabelProps={{ shrink: true }}
            value={ltoExpiry}
            disabled={disabled}
            error={Boolean(errors.ltoExpiry)}
            // V1 is an absolute time only — a relative ("in 3 days") or token-driven expiry are
            // follow-ups; a Jinja string token can't produce the number type expiration_time_ms
            // needs on the wire.
            helperText={errors.ltoExpiry || 'The exact date and time the offer countdown ends.'}
            onChange={(e) => { setLtoExpiry(e.target.value); clearError('ltoExpiry'); }}
            data-testid="bloomreach-standard-send-lto-expiry"
          />
        </div>
      )}
    </SendPanelShell>
  );
};

export default StandardSendPanel;
